import { useEffect, useRef } from 'react'
import cx from 'classnames'
import { debounceTime, fromEvent } from 'rxjs'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import { links } from '@/utils/link'
import { type UpcomingConsultation } from '@/features/jobs/list/UpcomingConsultationPreviewViewModel'
import styles from './UpcomingConsultationPreview.module.css'

export interface UpcomingConsultationPreviewProps {
  isExpanded: boolean
  upcomingConsultations: UpcomingConsultation[]
  onToggleExpanded: () => unknown
  onRowWidthChange: (width: number) => unknown
}

export function UpcomingConsultationPreview({
  isExpanded,
  upcomingConsultations,
  onToggleExpanded,
  onRowWidthChange,
}: UpcomingConsultationPreviewProps) {
  const nextConsultation = upcomingConsultations[0]
  const rowRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const subscription = fromEvent(window, 'resize')
      .pipe(debounceTime(50))
      .subscribe(() => {
        if (rowRef.current) {
          onRowWidthChange(rowRef.current.clientWidth)
        }
      })
    return () => subscription.unsubscribe()
  }, [onRowWidthChange])

  return (
    <div className={styles.root}>
      <div className={cx(styles.container, isExpanded && styles.expanded)}>
        <div className={styles.row} ref={rowRef}>
          <div className={styles.text}>
            <h3 className={styles.title}>Upcoming calls</h3>
          </div>
          <div className={cx(styles.nextCallItem, isExpanded && styles.hidden)}>
            <Icon color="var(--grey600)" name="call-video-on" size={32} />
            <div className={styles.callItemText}>
              <Button
                label={nextConsultation?.clientDisplayName}
                variant="text"
                className={styles.nextCallItemButton}
                to={links.job(nextConsultation?.jobId ?? '')}
              />
              <p className={styles.callItemDate}>
                {nextConsultation?.consultationStartDate}
              </p>
            </div>
          </div>
          <IconButton
            name="arrow"
            label={
              isExpanded ? 'Collapse consultations' : 'Expand consultations'
            }
            className={styles.accordionButton}
            onPress={onToggleExpanded}
            testId="upcoming-consultation-preview-expand-toggle"
            size={24}
            rotate={isExpanded ? 270 : 90}
            color="var(--grey600)"
          />
        </div>
        <div
          className={cx(
            styles.row,
            styles.accordion,
            !isExpanded && styles.hidden,
          )}
        >
          {upcomingConsultations.map((c, index) => (
            <div
              className={cx(styles.callItem, !isExpanded && styles.hidden)}
              key={index}
            >
              <div className={styles.callItemText}>
                <h5 className={styles.callItemTitle}>{c.clientDisplayName}</h5>
                <p className={styles.callItemDate}>{c.consultationStartDate}</p>
              </div>
              <Button
                label="View Job"
                variant="primary"
                to={links.job(c.jobId)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
